import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditorModule } from '@tinymce/tinymce-angular';
import { ProjectService, Project } from '../../services/project.service';
import { WebsiteService, SupportedLanguage, LanguageOption } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { ImageUploadService } from '../../services/image-upload.service';
import { MediaSelectorModalComponent } from '../pages/page-editor/components/media-selector-modal/media-selector-modal.component';
import { ProjectSelectorModalComponent } from '../pages/page-editor/components/project-selector-modal/project-selector-modal.component';
import { SafeUrlPipe } from '../../pipes/safe-url.pipe';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-edit-project',
  standalone: true,
  imports: [CommonModule, FormsModule, EditorModule, NotificationComponent, MediaSelectorModalComponent, ProjectSelectorModalComponent, SafeUrlPipe],
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss']
})
export class EditProjectComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  project: Partial<Project> = {};
  projectId: string | null = null;
  isNewProject = false;
  isLoading = false;
  isSaving = false;
  
  // Languages
  currentLocale: SupportedLanguage = 'nl';
  availableLanguages: LanguageOption[] = [];
  
  // Domains
  availableDomains: string[] = [];
  
  // Slug
  slugManuallyEdited = false;

  /**
   * URL to view this project on the Vercel preview deployment.
   * Uses ?domain= override so the right tenant's content is shown
   * (and middleware disables cache for instant updates).
   */
  get previewUrl(): string | null {
    const slug = this.project?.slug;
    if (!slug) return null;
    const domain = this.websiteService.getCurrentDomain();
    if (!domain) return null;
    return `${environment.previewBaseUrl}/projecten/${encodeURIComponent(slug)}?domain=${encodeURIComponent(domain)}`;
  }

  // Main Category (Hoofdcategorie)
  existingMainCategories: string[] = [];
  showNewMainCategoryInput = false;
  newMainCategoryValue = '';

  // Categories (Subcategorieën)
  existingCategories: string[] = [];
  showNewCategoryInput = false;
  newCategoryValue = '';
  
  // Image upload
  isUploading = false;
  uploadProgress = 0;
  uploadStatus = '';
  showMediaSelector = false;
  mediaSelectorTarget: 'main' | 'gallery' | 'before' | 'after' = 'main';
  
  // Related projects
  showRelatedProjectsSelector = false;
  allProjects: Project[] = [];
  
  // Editor config
  editorConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    promotion: false,
    skin: 'oxide-dark',
    content_css: 'dark',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | blocks | ' +
      'bold italic forecolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ' +
      'image media table | help',
    height: 300,
    content_style: 'body { background-color: #1F2937; color: #D1D5DB; }'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private websiteService: WebsiteService,
    private notificationService: NotificationService,
    private imageUploadService: ImageUploadService
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    
    // Load available domains
    this.availableDomains = await this.websiteService.getWebsites();
    
    // Load available languages for current domain
    const currentDomain = this.websiteService.getCurrentDomain();
    if (currentDomain) {
      const settings = await this.websiteService.getWebsiteSettings(currentDomain);
      if (settings?.availableLanguages && settings.availableLanguages.length > 0) {
        this.availableLanguages = settings.availableLanguages;
        const storedLang = this.websiteService.getCurrentLanguage();
        const availableCodes = this.availableLanguages.map(l => l.code);
        if (availableCodes.includes(storedLang)) {
          this.currentLocale = storedLang;
        } else {
          this.currentLocale = (settings.defaultLanguage || settings.availableLanguages[0].code) as SupportedLanguage;
        }
      } else {
        this.availableLanguages = [
          { code: 'nl', name: 'Nederlands' },
          { code: 'en', name: 'English' }
        ];
      }
    } else {
      this.availableLanguages = [
        { code: 'nl', name: 'Nederlands' },
        { code: 'en', name: 'English' }
      ];
    }
    
    // Load existing main categories + subcategories
    this.loadCategories();
    
    // Check if editing existing or creating new
    this.projectId = this.route.snapshot.paramMap.get('id');
    
    if (this.projectId && this.projectId !== 'new') {
      await this.loadProject();
    } else {
      this.isNewProject = true;
      this.project = this.getEmptyProject();
      // Set current domain by default
      if (currentDomain) {
        this.project.domains = [currentDomain];
      }
      this.isLoading = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getEmptyProject(): Partial<Project> {
    const emptyProject: Partial<Project> = {
      title: '',
      short_description: '',
      description: '',
      image: '',
      gallery: [],
      beforeImages: [],
      afterImages: [],
      youtube: '',
      location: '',
      client: '',
      application: '',
      plantingScheme: '',
      extra: '',
      relatedProjectIds: [],
      slug: '',
      mainCategory: '',
      categories: [],
      tags: [],
      url: '',
      featured: false,
      domains: []
    };
    
    // Initialize all language variants
    this.availableLanguages.forEach(lang => {
      emptyProject[`title_${lang.code}`] = '';
      emptyProject[`short_description_${lang.code}`] = '';
      emptyProject[`description_${lang.code}`] = '';
      emptyProject[`location_${lang.code}`] = '';
      emptyProject[`client_${lang.code}`] = '';
      emptyProject[`application_${lang.code}`] = '';
      emptyProject[`plantingScheme_${lang.code}`] = '';
      emptyProject[`extra_${lang.code}`] = '';
      emptyProject[`seo_title_${lang.code}`] = '';
      emptyProject[`seo_description_${lang.code}`] = '';
      emptyProject[`seo_keywords_${lang.code}`] = '';
    });

    return emptyProject;
  }

  async loadProject() {
    if (!this.projectId) return;

    try {
      const project = await this.projectService.getProjectById(this.projectId);
      if (project) {
        this.project = project;
        // Initialize arrays if needed
        if (!this.project.gallery) {
          this.project.gallery = [];
        }
        if (!this.project.beforeImages) {
          this.project.beforeImages = [];
        }
        if (!this.project.afterImages) {
          this.project.afterImages = [];
        }
        if (!this.project.relatedProjectIds) {
          this.project.relatedProjectIds = [];
        }
        if (!this.project.categories) {
          this.project.categories = [];
        }
        if (!this.project.mainCategory) {
          this.project.mainCategory = '';
        }
        if (!this.project.slug) {
          this.project.slug = this.generateSlug(this.project.title_nl || this.project.title || '');
        } else {
          this.slugManuallyEdited = true;
        }
      } else {
        this.notificationService.show({
          message: 'Project niet gevonden',
          type: 'error'
        });
        this.router.navigate(['/projects']);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      this.notificationService.show({
        message: 'Fout bij laden van project',
        type: 'error'
      });
      this.router.navigate(['/projects']);
    } finally {
      this.isLoading = false;
    }
  }

  loadCategories() {
    this.projectService.getProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe(projects => {
        const categories = new Set<string>();
        const mainCategories = new Set<string>();
        projects.forEach(p => {
          // Collect subcategories
          if (p.categories && Array.isArray(p.categories)) {
            p.categories.forEach(cat => {
              if (cat && cat.trim()) {
                categories.add(cat.trim());
              }
            });
          }
          // Collect main categories
          if (p.mainCategory && p.mainCategory.trim()) {
            mainCategories.add(p.mainCategory.trim());
          }
        });
        this.existingCategories = Array.from(categories).sort();
        this.existingMainCategories = Array.from(mainCategories).sort();
      });
  }

  // Slug helpers
  generateSlug(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  onTitleChange() {
    if (!this.slugManuallyEdited && this.currentLocale === 'nl') {
      this.project.slug = this.generateSlug(this.project.title_nl || '');
    }
  }

  onSlugChange() {
    this.slugManuallyEdited = true;
    // Forceer geldige slug-format
    if (this.project.slug) {
      this.project.slug = this.generateSlug(this.project.slug);
    }
  }

  regenerateSlug() {
    this.project.slug = this.generateSlug(this.project.title_nl || this.project.title || '');
    this.slugManuallyEdited = false;
  }

  // Language helpers
  setEditLanguage(lang: string) {
    this.currentLocale = lang as SupportedLanguage;
  }

  // Main Category management
  setMainCategory(category: string) {
    this.project.mainCategory = category;
  }

  clearMainCategory() {
    this.project.mainCategory = '';
  }

  showAddNewMainCategory() {
    this.showNewMainCategoryInput = true;
    this.newMainCategoryValue = '';
  }

  addNewMainCategory() {
    if (this.newMainCategoryValue.trim()) {
      const newCat = this.newMainCategoryValue.trim();
      if (!this.existingMainCategories.includes(newCat)) {
        this.existingMainCategories = [...this.existingMainCategories, newCat].sort();
      }
      this.project.mainCategory = newCat;
    }
    this.showNewMainCategoryInput = false;
    this.newMainCategoryValue = '';
  }

  cancelNewMainCategory() {
    this.showNewMainCategoryInput = false;
    this.newMainCategoryValue = '';
  }

  // Subcategory management
  toggleCategory(category: string) {
    if (!this.project.categories) {
      this.project.categories = [];
    }
    const index = this.project.categories.indexOf(category);
    if (index > -1) {
      this.project.categories.splice(index, 1);
    } else {
      this.project.categories.push(category);
    }
  }

  isCategorySelected(category: string): boolean {
    return (this.project.categories || []).includes(category);
  }

  showAddNewCategory() {
    this.showNewCategoryInput = true;
    this.newCategoryValue = '';
  }

  addNewCategory() {
    if (this.newCategoryValue.trim()) {
      const newCat = this.newCategoryValue.trim();
      if (!this.existingCategories.includes(newCat)) {
        this.existingCategories = [...this.existingCategories, newCat].sort();
      }
      if (!this.project.categories) {
        this.project.categories = [];
      }
      if (!this.project.categories.includes(newCat)) {
        this.project.categories.push(newCat);
      }
    }
    this.showNewCategoryInput = false;
    this.newCategoryValue = '';
  }

  cancelNewCategory() {
    this.showNewCategoryInput = false;
    this.newCategoryValue = '';
  }

  removeCategory(category: string) {
    if (!this.project.categories) return;
    const index = this.project.categories.indexOf(category);
    if (index > -1) {
      this.project.categories.splice(index, 1);
    }
  }

  // Domain management
  isDomainSelected(domain: string): boolean {
    return (this.project.domains || []).includes(domain);
  }

  toggleDomainSelection(domain: string) {
    if (!this.project.domains) {
      this.project.domains = [];
    }
    const index = this.project.domains.indexOf(domain);
    if (index > -1) {
      this.project.domains.splice(index, 1);
    } else {
      this.project.domains.push(domain);
    }
  }

  // Tags management
  addTag(tagInput: HTMLInputElement) {
    const tag = tagInput.value.trim();
    if (!tag) return;
    if (!this.project.tags) this.project.tags = [];
    if (!this.project.tags.includes(tag)) {
      this.project.tags.push(tag);
    }
    tagInput.value = '';
  }

  removeTag(tag: string) {
    if (!this.project.tags) return;
    const index = this.project.tags.indexOf(tag);
    if (index > -1) {
      this.project.tags.splice(index, 1);
    }
  }

  // Save/Cancel
  async saveProject() {
    // Validate
    if (!this.project.title_nl?.trim()) {
      this.notificationService.show({
        message: 'Voer een titel in (NL)',
        type: 'error'
      });
      return;
    }

    this.isSaving = true;

    try {
      if (this.isNewProject) {
        await this.projectService.createProject(this.project as Omit<Project, 'id'>);
        this.notificationService.show({
          message: 'Project succesvol aangemaakt',
          type: 'success'
        });
      } else if (this.projectId) {
        await this.projectService.updateProject(this.projectId, this.project);
        this.notificationService.show({
          message: 'Project succesvol bijgewerkt',
          type: 'success'
        });
      }
      
      setTimeout(() => {
        this.router.navigate(['/projects']);
      }, 500);
    } catch (error) {
      console.error('Error saving project:', error);
      this.notificationService.show({
        message: 'Fout bij opslaan van project',
        type: 'error'
      });
    } finally {
      this.isSaving = false;
    }
  }

  cancel() {
    this.router.navigate(['/projects']);
  }

  // Main Image upload
  async onMainImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    
    if (!file.type.startsWith('image/')) {
      this.notificationService.show({
        message: 'Selecteer een afbeelding',
        type: 'error'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.notificationService.show({
        message: 'Afbeelding mag maximaal 10MB zijn',
        type: 'error'
      });
      return;
    }

    try {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadStatus = 'Voorbereiden...';

      const variants = await this.imageUploadService.uploadImageWithVariants(
        file,
        (progress) => {
          this.uploadProgress = progress.progress;
          this.uploadStatus = progress.status;
        }
      );

      this.project.image = variants.webp1400;
      this.project.imageVariants = variants;

      this.notificationService.show({
        message: 'Hoofdafbeelding succesvol geüpload',
        type: 'success'
      });

      input.value = '';
    } catch (error) {
      console.error('Error uploading image:', error);
      this.notificationService.show({
        message: 'Fout bij uploaden van afbeelding',
        type: 'error'
      });
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.uploadStatus = '';
    }
  }

  removeMainImage() {
    this.project.image = '';
    this.project.imageVariants = undefined;
  }

  // Gallery management
  async onGalleryImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;

      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadStatus = `Uploaden: ${file.name}...`;

        const variants = await this.imageUploadService.uploadImageWithVariants(
          file,
          (progress) => {
            this.uploadProgress = progress.progress;
            this.uploadStatus = `${file.name}: ${progress.status}`;
          }
        );

        if (!this.project.gallery) {
          this.project.gallery = [];
        }
        this.project.gallery.push({
          url: variants.webp1400,
          variants: variants
        });
      } catch (error) {
        console.error('Error uploading gallery image:', error);
      }
    }

    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadStatus = '';
    input.value = '';

    this.notificationService.show({
      message: 'Galerij afbeeldingen geüpload',
      type: 'success'
    });
  }

  removeGalleryImage(index: number) {
    if (this.project.gallery) {
      this.project.gallery.splice(index, 1);
    }
  }

  // Before/After images management
  async onBeforeImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;

      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadStatus = `Uploaden: ${file.name}...`;

        const variants = await this.imageUploadService.uploadImageWithVariants(
          file,
          (progress) => {
            this.uploadProgress = progress.progress;
            this.uploadStatus = `${file.name}: ${progress.status}`;
          }
        );

        if (!this.project.beforeImages) {
          this.project.beforeImages = [];
        }
        this.project.beforeImages.push({
          url: variants.webp1400,
          variants: variants
        });
      } catch (error) {
        console.error('Error uploading before image:', error);
      }
    }

    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadStatus = '';
    input.value = '';

    this.notificationService.show({
      message: 'Voor afbeeldingen geüpload',
      type: 'success'
    });
  }

  removeBeforeImage(index: number) {
    if (this.project.beforeImages) {
      this.project.beforeImages.splice(index, 1);
    }
  }

  async onAfterImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;

      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadStatus = `Uploaden: ${file.name}...`;

        const variants = await this.imageUploadService.uploadImageWithVariants(
          file,
          (progress) => {
            this.uploadProgress = progress.progress;
            this.uploadStatus = `${file.name}: ${progress.status}`;
          }
        );

        if (!this.project.afterImages) {
          this.project.afterImages = [];
        }
        this.project.afterImages.push({
          url: variants.webp1400,
          variants: variants
        });
      } catch (error) {
        console.error('Error uploading after image:', error);
      }
    }

    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadStatus = '';
    input.value = '';

    this.notificationService.show({
      message: 'Na afbeeldingen geüpload',
      type: 'success'
    });
  }

  removeAfterImage(index: number) {
    if (this.project.afterImages) {
      this.project.afterImages.splice(index, 1);
    }
  }

  // Media selector
  openMediaSelector(target: 'main' | 'gallery' | 'before' | 'after') {
    this.mediaSelectorTarget = target;
    this.showMediaSelector = true;
  }

  closeMediaSelector() {
    this.showMediaSelector = false;
  }

  onMediaSelected(url: string) {
    if (this.mediaSelectorTarget === 'main') {
      this.project.image = url;
    } else if (this.mediaSelectorTarget === 'gallery') {
      if (!this.project.gallery) {
        this.project.gallery = [];
      }
      this.project.gallery.push({ url });
    } else if (this.mediaSelectorTarget === 'before') {
      if (!this.project.beforeImages) {
        this.project.beforeImages = [];
      }
      this.project.beforeImages.push({ url });
    } else if (this.mediaSelectorTarget === 'after') {
      if (!this.project.afterImages) {
        this.project.afterImages = [];
      }
      this.project.afterImages.push({ url });
    }
    this.closeMediaSelector();
  }

  // YouTube helpers
  getYouTubeEmbedUrl(): string | null {
    if (!this.project.youtube) return null;
    const videoId = this.extractYouTubeId(this.project.youtube);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }

  extractYouTubeId(url: string): string | null {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  // Related projects management
  async loadAllProjects() {
    this.projectService.getProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe(projects => {
        // Filter out current project
        this.allProjects = projects.filter(p => p.id !== this.projectId);
      });
  }

  openRelatedProjectsSelector() {
    this.loadAllProjects();
    this.showRelatedProjectsSelector = true;
  }

  closeRelatedProjectsSelector() {
    this.showRelatedProjectsSelector = false;
  }

  onRelatedProjectsSelected(projectIds: string[]) {
    this.project.relatedProjectIds = projectIds;
    this.closeRelatedProjectsSelector();
  }

  isRelatedProject(projectId: string | undefined): boolean {
    if (!projectId || !this.project.relatedProjectIds) return false;
    return this.project.relatedProjectIds.includes(projectId);
  }

  removeRelatedProject(projectId: string) {
    if (!this.project.relatedProjectIds) return;
    const index = this.project.relatedProjectIds.indexOf(projectId);
    if (index > -1) {
      this.project.relatedProjectIds.splice(index, 1);
    }
  }

  getRelatedProjectTitle(projectId: string): string {
    const project = this.allProjects.find(p => p.id === projectId);
    if (!project) return 'Onbekend project';
    return project.title_nl || project.title_en || project.title || 'Onbekend';
  }
}
